import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Car,
  Home as HomeIcon,
  HeartPulse,
  Stethoscope,
  Building2,
  Truck,
  Zap,
  ArrowRight,
  Phone,
  Mail,
  Check,
} from "lucide-react";
import { BRAND, SERVICES } from "../data/brand";
import EnergiaPreventivoForm from "../components/EnergiaPreventivoForm";

const ICONS = { Car, Home: HomeIcon, HeartPulse, Stethoscope, Building2, Truck, Zap };

export default function Servizi() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [hash]);

  return (
    <div data-testid="servizi-page">
      {/* Hero */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="container-90 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="overline mb-6">I nostri servizi</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.02]">
              Sette aree di tutela,{" "}
              <span className="text-[color:var(--brand-blue)]">pensate per ogni fase della tua vita.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-3xl">
              Dalla polizza auto al risparmio in bolletta: costruiamo soluzioni complete
              confrontando le migliori compagnie e fornitori del mercato.
            </p>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            {SERVICES.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`servizi-quicknav-${s.id}`}
                className="px-5 py-2.5 bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)] transition-colors"
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed services */}
      <section className="bg-white">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon] || HomeIcon;
          const reverse = i % 2 === 1;
          const isEnergia = !!s.energiaForm;
          return (
            <div
              key={s.id}
              id={s.id}
              data-testid={`servizio-${s.id}-section`}
              className={`border-b border-slate-200 ${i === SERVICES.length - 1 ? "border-b-0" : ""} ${
                isEnergia ? "bg-emerald-50/40" : ""
              }`}
            >
              <div className="container-90 py-16 lg:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                  <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                    <div className="relative aspect-[4/3] max-w-md overflow-hidden bg-slate-100">
                      <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                      <div className="absolute top-4 left-4 w-11 h-11 bg-white flex items-center justify-center">
                        <Icon className={`w-5 h-5 ${isEnergia ? "text-emerald-700" : "text-[color:var(--brand-blue)]"}`} />
                      </div>
                    </div>
                  </div>

                  <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
                    <div className={`overline mb-4 ${isEnergia ? "text-emerald-700" : ""}`}>0{i + 1} · Area di tutela</div>
                    <h2 className="font-heading text-3xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-6">
                      {s.title}
                    </h2>
                    <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-2xl">
                      {s.description}
                    </p>

                    {/* Feature list (only when present, e.g. Trasporto) */}
                    {s.features && s.features.length > 0 && (
                      <div data-testid={`servizio-${s.id}-features`} className="mb-10">
                        <div className="overline mb-4">Garanzie principali</div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                          {s.features.map((f, idx) => (
                            <li key={idx} className="flex gap-3 items-start">
                              <div className="flex-shrink-0 w-5 h-5 bg-[color:var(--brand-blue)] flex items-center justify-center mt-0.5">
                                <Check className="w-3 h-3 text-white" strokeWidth={3} />
                              </div>
                              <span className="text-slate-700 text-[0.95rem]">{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {!isEnergia && (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <a
                          href={`tel:${BRAND.phoneRaw}`}
                          data-testid={`servizio-${s.id}-call-btn`}
                          className="btn-primary"
                        >
                          <Phone className="w-4 h-4" /> Richiedi consulenza
                        </a>
                        <a
                          href={`mailto:${BRAND.email}?subject=Preventivo ${s.title}`}
                          data-testid={`servizio-${s.id}-email-btn`}
                          className="btn-outline"
                        >
                          <Mail className="w-4 h-4" /> Preventivo gratuito
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Energia inline form */}
                {isEnergia && (
                  <div className="mt-12 lg:mt-16">
                    <EnergiaPreventivoForm />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {/* CTA block */}
      <section className="bg-slate-50 border-t border-slate-200 section">
        <div className="container-90">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="overline mb-3">Non trovi quello che cerchi?</div>
              <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Studiamo anche soluzioni personalizzate.
              </h2>
            </div>
            <div className="lg:text-right">
              <Link to="/contatti" className="btn-primary" data-testid="servizi-bottom-cta">
                Parla con noi <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
